import isItemView from 'owa-mail-store/lib/utils/isItemView';
import shouldPrefetchItem from './shouldPrefetchItem';
import TableView from 'mini-mail-list-store/lib/store/schema/TableView';
import { createRequestQueue, RequestQueue } from 'owa-service-utils';
import { getItemReadingPaneViewState } from 'mini-mail-reading-pane-store/lib/store/selectors/getItemReadingPaneViewState';
import { lazyLoadConversation, lazyLoadItem } from 'mini-mail-load-conversation';
import { LoadConversationItemActionSource } from 'owa-mail-store/lib/store/schema/LoadConversationItemActionSource';

const MAXIMUM_NUM_REQUESTS = 3;
let requestQueue: RequestQueue = null;

/**
 * Return an instance of RequestQueue
 * Allows for MAXIMUM_NUM_REQUESTS in parallel and queues the rest for execution
 */
const getRequestQueue = () => {
    if (!requestQueue) {
        requestQueue = createRequestQueue(MAXIMUM_NUM_REQUESTS);
    }

    return requestQueue;
};

/**
 * Issues load conversation/item to make a service call for fetching data
 * @param - rowId - id of the row that needs to be prefetched
 * @param - tableView - tableView to which the item belongs
 * @param - updateIfExists - True if this was called as a result of row add or modified notification; false otherwise (prefetch)
 */
export default function prefetchItem(
    rowId: string,
    tableView: TableView,
    updateIfExists: boolean,
    actionSource: LoadConversationItemActionSource
) {
    if (shouldPrefetchItem(rowId, tableView, updateIfExists)) {
        if (isItemView(tableView)) {
            handleLoadItem(rowId, actionSource);
        } else {
            // If item-reading-pane is open in conversation view and we get an SSE (PrefetchSingleRow),
            // we would want to update the item data.
            // Skip the load of item data for other prefetch scenarios.
            // TODO: Decouple prefetch of data from refreshing the data when we get SSE events for clarity
            if (actionSource === LoadConversationItemActionSource.PrefetchSingleRow) {
                const { itemId } = getItemReadingPaneViewState() || {};
                itemId && handleLoadItem(itemId, actionSource);
            }

            lazyLoadConversation
                .import()
                .then(loadConversation => getRequestQueue().add(() => loadConversation(rowId)));
        }
    }
}

const handleLoadItem = (itemId: string, actionSource: LoadConversationItemActionSource) =>
    lazyLoadItem
        .import()
        .then(loadItem => getRequestQueue().add(() => loadItem(itemId, actionSource)));
